import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { Size } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import styles from './icon-button.scss?inline'

const IconButtonBaseWithSized = SizedMixin(DdsElement)

export type ButtonVariant = 'neutral' | 'brand' | 'inverted' | 'inverted-brand'
export type HtmlButtonType = 'button' | 'submit' | 'reset'
export type ButtonSize = Extract<Size, 'lg' | 'md' | 'sm' | 'xs'>

/**
 * `dap-ds-icon-button`
 * @summary An icon button is a button with an icon.
 * @element dap-ds-icon-button
 * @title - Icon button
 *
 * @property {string} ariaLabel - The aria label of the button.
 *
 * @slot - The icon of the button.
 *
 * @csspart base - The main button container.
 * @csspart content - The icon of the button.
 * @csspart icon - The icon of the icon.
 * @csspart icon-base - The base of the icon.
 *
 * @cssproperty --dds-icon-button-border-width - Border width of the icon button (default: var(--dds-border-width-base))
 * @cssproperty --dds-icon-button-border-color - Border color of the icon button (default: var(--dds-border-neutral-transparent-interactive, #fff500))
 * @cssproperty --dds-icon-button-border-radius - Border radius of the icon button (default: var(--dds-radius-rounded))
 * @cssproperty --dds-icon-button-hover-border-width - Border width on hover (default: var(--dds-border-width-large))
 * @cssproperty --dds-icon-button-active-border-width - Border width when active (default: var(--dds-border-width-xlarge))
 * @cssproperty --dds-icon-button-transition - Transition property for the icon button (default: var(--dds-transition-all))
 * @cssproperty --dds-icon-button-cursor - Cursor style for the button (default: pointer)
 * @cssproperty --dds-icon-button-disabled-cursor - Cursor style for the disabled button (default: not-allowed)
 * @cssproperty --dds-icon-button-column-gap - Column gap between elements (default: var(--dds-spacing-100))
 * @cssproperty --dds-icon-button-padding-lg - Padding for large size (default: var(--dds-spacing-0))
 * @cssproperty --dds-icon-button-padding-md - Padding for medium size (default: var(--dds-spacing-100))
 * @cssproperty --dds-icon-button-padding-sm - Padding for small size (default: var(--dds-spacing-50))
 * @cssproperty --dds-icon-button-padding-xs - Padding for extra small size (default: var(--dds-spacing-25))
 * @cssproperty --dds-icon-button-size-lg - Size for large icon button (default: var(--dds-spacing-800))
 * @cssproperty --dds-icon-button-size-md - Size for medium icon button (default: var(--dds-spacing-600))
 * @cssproperty --dds-icon-button-size-sm - Size for small icon button (default: var(--dds-spacing-400))
 * @cssproperty --dds-icon-button-size-xs - Size for extra small icon button (default: var(--dds-spacing-300))
 * @cssproperty --dds-icon-button-neutral-bg - Background color for neutral variant (default: var(--dds-transparent-black-subtle))
 * @cssproperty --dds-icon-button-neutral-color - Text color for neutral variant (default: var(--dds-icon-neutral-base))
 * @cssproperty --dds-icon-button-neutral-hover-bg - Hover background color for neutral variant (default: var(--dds-transparent-black-base))
 * @cssproperty --dds-icon-button-neutral-active-bg - Active background color for neutral variant (default: var(--dds-transparent-black-medium))
 * @cssproperty --dds-icon-button-brand-bg - Background color for brand variant (default: var(--dds-button-subtle-background-enabled))
 * @cssproperty --dds-icon-button-brand-color - Text color for brand variant (default: var(--dds-button-subtle-icon-enabled))
 * @cssproperty --dds-icon-button-brand-hover-bg - Hover background color for brand variant (default: var(--dds-button-subtle-background-hover))
 * @cssproperty --dds-icon-button-brand-active-bg - Active background color for brand variant (default: var(--dds-button-subtle-background-pressed))
 * @cssproperty --dds-icon-button-inverted-bg - Background color for inverted variant (default: var(--dds-transparent-white-subtle))
 * @cssproperty --dds-icon-button-inverted-color - Text color for inverted variant (default: var(--dds-button-primary-icon-enabled))
 * @cssproperty --dds-icon-button-inverted-hover-bg - Hover background color for inverted variant (default: var(--dds-transparent-white-base))
 * @cssproperty --dds-icon-button-inverted-active-bg - Active background color for inverted variant (default: var(--dds-transparent-white-medium))
 * @cssproperty --dds-icon-button-inverted-brand-bg - Background color for inverted brand variant (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-icon-button-inverted-brand-color - Text color for inverted brand variant (default: var(--dds-button-primary-icon-enabled))
 * @cssproperty --dds-icon-button-inverted-brand-hover-bg - Hover background color for inverted brand variant (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-icon-button-inverted-brand-active-bg - Active background color for inverted brand variant (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-icon-button-disabled-color - Color for disabled state (default: var(--dds-icon-neutral-disabled))
 */
@customElement('dap-ds-icon-button')
export default class DapDSIconButton extends IconButtonBaseWithSized {
  /** The type of the button.
   * @type {'neutral' | 'brand' | 'inverted' | 'inverted-brand'}
   */
  @property({ reflect: true }) variant?: ButtonVariant = 'neutral'

  /** Adds a transparent background to the button */
  @property({ type: Boolean, reflect: true }) clean? = false

  /**
   * The size of the icon in pixels. By default the icon gets the size of the button. This property overwrites the size of the icon.
   */
  @property({ type: Number }) iconSize: number

  constructor() {
    super()
    this.size = 'md' as ButtonSize
  }

  /** The disabled state of the button. */
  @property({ type: Boolean, reflect: true }) disabled = false

  /**
   * The type of the button.
   * @type {'button' | 'submit' | 'reset'}
   */
  @property() htmlType: HtmlButtonType = 'button'

  /** The icon of the button. You can use the icon names from the icon component */
  @property() icon: string

  /** The name of the button */
  @property() name: string

  static readonly styles = unsafeCSS(styles)

  /** Persisted aria-label for the inner button; host attribute is removed so we keep the value here */
  private _ariaLabelForButton: string | null = null

  /** Aria-label for the inner button; survives host attribute removal so Lit does not clear it */
  private get effectiveAriaLabel(): string | null {
    return this.ariaLabel ?? this._ariaLabelForButton
  }

  /**
   * Recursively finds all icon elements in slots and nested slots
   */
  private findIconElementsInSlot(slot: HTMLSlotElement): Element[] {
    const iconElements: Element[] = []

    // Get both assigned elements and nodes (to catch all content)
    const assignedElements = slot.assignedElements({ flatten: true })
    const assignedNodes = slot.assignedNodes({ flatten: true })

    // First, check assigned elements
    for (const element of assignedElements) {
      // If it's an icon element, add it
      if (element.tagName === 'DAP-DS-ICON') {
        iconElements.push(element)
      }
      // If it's a nested slot, recursively find icons in it
      else if (element instanceof HTMLSlotElement) {
        iconElements.push(...this.findIconElementsInSlot(element))
      }
      // If it's a custom element, search for icons in its light DOM children
      // (slot default content appears in light DOM)
      else if (element.children) {
        const lightDomIcons = Array.from(
          element.querySelectorAll('dap-ds-icon'),
        )
        iconElements.push(...lightDomIcons)
      }
    }

    // Also check assigned nodes for icon elements (in case they're not in assignedElements)
    for (const node of assignedNodes) {
      if (node instanceof Element && node.tagName === 'DAP-DS-ICON') {
        if (!iconElements.includes(node)) {
          iconElements.push(node)
        }
      }
      // Check children of element nodes
      else if (node instanceof Element) {
        const childIcons = Array.from(node.querySelectorAll('dap-ds-icon'))
        for (const icon of childIcons) {
          if (!iconElements.includes(icon)) {
            iconElements.push(icon)
          }
        }
      }
    }

    return iconElements
  }

  /**
   * Applies size props to all icon elements in the slot
   */
  private applySizeToSlotIcons(): void {
    if (!this.shadowRoot) return

    const iconSlot = this.shadowRoot.querySelector('slot') as HTMLSlotElement
    if (!iconSlot) return

    const iconElements = this.findIconElementsInSlot(iconSlot)

    for (const iconElement of iconElements) {
      if (this.iconSize !== undefined) {
        iconElement.setAttribute('staticSize', this.iconSize.toString())
        iconElement.setAttribute('size', this.iconSize.toString())
      } else {
        iconElement.setAttribute('size', this.effectiveSize)
        iconElement.removeAttribute('staticSize')
      }
    }
  }

  /**
   * Handles slot change events to update icon sizes
   */
  private handleSlotChange = (): void => {
    // Set up listeners on any newly assigned nested slots
    if (this.shadowRoot) {
      const iconSlot = this.shadowRoot.querySelector('slot') as HTMLSlotElement
      if (iconSlot) {
        const assignedElements = iconSlot.assignedElements({ flatten: true })
        for (const element of assignedElements) {
          if (element instanceof HTMLSlotElement) {
            // Check if we already have a listener on this slot
            if (!(element as any).__ddsSlotListenerAdded) {
              element.addEventListener('slotchange', this.handleSlotChange)
              ;(element as any).__ddsSlotListenerAdded = true
            }
          }
        }
      }
    }

    // Apply size to icons
    this.applySizeToSlotIcons()
  }

  /**
   * Sets up slotchange listeners for the main slot and any nested slots
   */
  private setupSlotListeners(slot: HTMLSlotElement): void {
    slot.addEventListener('slotchange', this.handleSlotChange)

    // Also listen to nested slots that are already assigned
    const assignedElements = slot.assignedElements({ flatten: true })
    for (const element of assignedElements) {
      if (element instanceof HTMLSlotElement) {
        element.addEventListener('slotchange', this.handleSlotChange)
        ;(element as any).__ddsSlotListenerAdded = true
      }
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    if (this.shadowRoot) {
      const iconSlot = this.shadowRoot.querySelector('slot') as HTMLSlotElement
      if (iconSlot) {
        this.setupSlotListeners(iconSlot)
      }
    }

    // Use requestAnimationFrame to ensure slots are fully assigned
    requestAnimationFrame(() => {
      this.applySizeToSlotIcons()
    })
  }

  protected updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(changedProperties)

    // Persist value before removing host attribute so the inner button keeps it
    if (this.ariaLabel != null && this.ariaLabel !== '') {
      this._ariaLabelForButton = this.ariaLabel
    } else if (changedProperties.has('ariaLabel')) {
      this._ariaLabelForButton = null
    }
    this.removeAttribute('aria-label')

    // Update slot icons when size, sizeMap, or iconSize changes (sizeMap drives effectiveSize on viewport change)
    if (
      changedProperties.has('size') ||
      changedProperties.has('sizeMap') ||
      changedProperties.has('iconSize') ||
      this.sizeMap
    ) {
      requestAnimationFrame(() => {
        this.applySizeToSlotIcons()
      })
    }
  }

  focus() {
    this.shadowRoot?.querySelector('button')?.focus()
  }

  render() {
    return html`
      <button
        part="base"
        .disabled=${this.disabled}
        type=${this.htmlType}
        aria-label=${ifDefined(this.effectiveAriaLabel) || ''}
        class=${clsx(
          'icon-button',
          {
            [`icon-button--${this.effectiveSize}`]:
              this.effectiveSize && this.iconSize === undefined,
          },
          { 'icon-button--disabled': this.disabled },
          { 'icon-button--clean': this.clean },
          `icon-button--${this.variant}`,
        )}>
        <div part="high-contrast" class="button__highcontrast"></div>
        ${this.icon
          ? html` <dap-ds-icon
              part="content"
              exportparts="icon, base: icon-base"
              name="${this.icon}"
              aria-hidden="true"
              staticSize=${ifDefined(this.iconSize)}
              size=${(this.effectiveSize === 'xxs'
                ? 'xs'
                : this.effectiveSize) as ButtonSize}>
            </dap-ds-icon>`
          : html` <slot></slot> `}
      </button>
    `
  }
}
